import Vue from 'vue';
import { fetchWithErrorHandling, fetchToJsonWithErrorHandling } from './common';

export interface IDevice {
  rowKey: string;
  name: string;
  serialKeyHash: string;
  available: boolean;
  accessTokenHash: boolean;
  timestamp: Date;
}

export interface NaviResult {
  title: string;
  item: NaviItem[];
}

export interface NaviItem {
  caption: string;
  link: string;
}

export interface ISerialKey {
  rowKey: string;
  serialKeyHash: string;
  expire?: Date;
  timestamp: Date;
}

export interface IAccessTokenResult
{
  accessToken: string;
  code: string;
}

export class SystemService {
  static appRoot: string | undefined = process.env.VUE_APP_URL;

  static apiRoot: string = process.env.VUE_APP_API_URL;

  static tokenKey: string = 'accessToken';

  static code: string = `?code=${process.env.VUE_APP_FUNCTION_KEY}`;

  /**
   * 登録されている有効なデバイスのリストを取得します。
   * @param serialKey シリアルキーのハッシュ。
   * @returns デバイスのリスト。
   */
  static async getDevices(serialKey: string): Promise<IDevice[]> {
    const url: string = `${SystemService.apiRoot}/devices/${serialKey}${SystemService.code}`;
    const accessToken: string | null = Vue.$cookies.get(SystemService.tokenKey);
    // sessionStorage.getItem(SystemService.tokenKey);

    return await fetchToJsonWithErrorHandling(url, {
      method: 'get',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((data: IDevice[]) => {
        console.log('data: ', data);
        return data;
      })
      .catch((error: Error) => {
        console.error('デバイスリストの取得に失敗しました。', error);
        throw error;
      });
  }

  static getPdfUrl(): string {
    return `${SystemService.apiRoot}/pdf${SystemService.code}`;
  }

  /**
   * ナビゲーションの目次情報を取得します。
   * @returns ナビゲーション項目
   */
  static async getNavi(): Promise<NaviResult> {
    const url: string = `${SystemService.apiRoot}/navi${SystemService.code}`;
    const accessToken: string | null = Vue.$cookies.get(SystemService.tokenKey);

    return await fetchToJsonWithErrorHandling(url, {
      method: 'get',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((data: NaviResult) => {
        console.log('data: ', data);
        return data;
      })
      .catch((error: Error) => {
        console.error('ナビゲーションリストの取得に失敗しました。', error);
        throw error;
      });
  }

  /**
   * PDF を取得します。
   * @returns PDF の BLOB オブジェクト。
   */
  static async getPdf(): Promise<Blob> {
    const accessToken: string | null = Vue.$cookies.get(SystemService.tokenKey);
    const url: string = SystemService.getPdfUrl();

    return await fetchWithErrorHandling(url, {
      method: 'get',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/pdf',
        'Content-Type': 'application/pdf',
      },
    })
      .then((data: Response) => {
        console.log('data', data);
        return data.blob();
      })
      .catch((error: Error) => {
        console.error('PDF の取得に失敗しました。', error);
        throw error;
      });
  }

  /**
   * デバイスを登録します。
   * @param serialKey シリアルキー
   * @param deviceName デバイス名
   * @returns 取得したアクセストークン
   */
  static async register(serialKey: string, deviceName: string): Promise<IAccessTokenResult> {
    const url: string = `${SystemService.apiRoot}/register${SystemService.code}`;
    console.log(url);

    return await fetchToJsonWithErrorHandling(url, {
      method: 'post',
      mode: 'cors',
      body: JSON.stringify({ serialKey, deviceName }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((data: IAccessTokenResult) => {
        console.log('data: ', data);
        switch (data.code) {
          case 'OK':
            // アクセス トークンをセッション ストレージにキャッシュする。
            // sessionStorage.setItem(SystemService.tokenKey, data.accessToken);
            Vue.$cookies.set(
              SystemService.tokenKey, // キー名
              data.accessToken, // 値
              60 * 60 * 24 * 365 * 2, // 2年間の有効期限
              '', // path
              '', // domain
              // true, // Secure
            );
            console.log('data.accessToken', data.accessToken);
            break;
          case 'MAX_DEVICES_COUNT':
            break;
          case 'NOT_FOUND':
            break;
          case 'EXPIRED':
            break;
          default:
        }
        return data;
      })
      .catch((error: Error) => {
        console.error('シリアルキーの登録に失敗しました。', error);
        return false;
      });
  }

  /**
   * デバイスの登録を解除します。
   * @param serialKey シリアルキー
   * @param deviceID 解除するデバイスの ID
   * @returns デバイスの登録が成功した場合は true 。それ以外の場合は false を返す。
   */
  static async unregister(serialKey: string, deviceID: string): Promise<boolean> {
    const url: string = `${SystemService.apiRoot}/unregister${SystemService.code}`;
    console.log(url);

    return await fetchWithErrorHandling(url, {
      method: 'post',
      mode: 'cors',
      body: JSON.stringify({ serialKey, deviceID }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .catch((error: Error) => {
        console.error('デバイスの登録解除に失敗しました。', error);
        return false;
      });
  }

  /**
   * アクセストークンで認証します。
   * @returns 認証が成功した場合は true 。それ以外の場合は false を返す。
   */
  static async authenticate(): Promise<boolean> {
    // const accessToken: string | null = sessionStorage.getItem(SystemService.tokenKey);
    const accessToken = Vue.$cookies.get(SystemService.tokenKey);
    if (!accessToken) {
      return false;
    }

    const url: string = `${SystemService.apiRoot}/auth${SystemService.code}`;
    console.log(url);

    return await fetchWithErrorHandling(url, {
      method: 'get',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then(() => true)
      .catch((error: Error) => {
        // セッションからトークンキーを削除する
        // sessionStorage.removeItem(SystemService.tokenKey);
        Vue.$cookies.remove(SystemService.tokenKey);
        console.log('アクセストークンを削除しました。', error);

        if (error.message === 'INVALID_TOKEN') {
          console.error('アクセストークンの認証に失敗しました。', error);
        }
        return false;
      });
  }
}
